import React, { useEffect, useRef, useState } from 'react';
import { deprecate } from '../../../lib/utils';
import { SearchBarCombinedProps, SearchBarStateEnum } from './types';

const usePresenter = (
  props: SearchBarCombinedProps,
): SearchBarCombinedProps => {
  if (props.state) {
    deprecate('The state prop is depreated for SearchBar.');
  }

  const { clearOnBlur, textValue, onTextChanged, onBlur, onFocus, onKeyDown, onSubmit, textPlaceholder } = props;

  const [state, setState] = useState<SearchBarStateEnum>('Unfilled');

  const [inputValue, setInputValue] = useState<string>('');
  const [submittedValue, setSubmittedValue] = useState<string>('');
  const [searchIsFocused, setSearchIsFocused] = useState<boolean>(false);

  const [displayPlaceholder, setDisplayPlaceholder] = useState<string | undefined>(textPlaceholder);

  useEffect(() => {
    setInputValue(textValue || '');
  }, [textValue]);

  const onInputChanged = (
    event: React.ChangeEvent<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setInputValue(event.target.value);
    if (onTextChanged) {
      onTextChanged(event.target.value);
    }
  };

  const searchBarRef = useRef<HTMLInputElement>(null);

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) {
      onKeyDown(e);
    }

    if (e.key !== 'Enter') {
      return;
    }

    setSubmittedValue(inputValue);

    if (searchBarRef.current) {
      searchBarRef.current.blur();
    }

    if (onSubmit) {
      onSubmit(inputValue);
    }
  };

  const clearSearch = (e?: React.MouseEvent<HTMLElement>) => {
    // If passed as a prop to button, do not bubble event
    if (e) {
      e.stopPropagation();
    }

    setSubmittedValue('');
    setInputValue('');
    if (onSubmit) {
      onSubmit('');
    }
  };

  useEffect(() => {
    setState(inputValue.length ? 'Filled' : 'Unfilled');
  }, [inputValue]);

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      onBlur(e);
    }
    // added to see if the blured happened within the search container or not
    if (e?.currentTarget.contains(e?.relatedTarget)) {
      return;
    }
    setSearchIsFocused(false);
  };

  const onFocusHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onFocus) {
      onFocus(e);
    }

    setSearchIsFocused(true);
  };

  useEffect(() => {
    if (searchIsFocused || !clearOnBlur) {
      return;
    }

    if (inputValue !== submittedValue) {
      clearSearch();
    }
  }, [searchIsFocused, inputValue, submittedValue]);

  useEffect(() => {
    setDisplayPlaceholder(searchIsFocused ? '' : textPlaceholder);
  }, [searchIsFocused]);

  props = {
    ...props,
    ref: searchBarRef,
    textValue: inputValue,
    onBlur: onBlurHandler,
    onFocus: onFocusHandler,
    onChange: onInputChanged,
    onKeyDown: onKeyDownHandler,
    state,
    clearSearch,
    textPlaceholder: displayPlaceholder,
  };

  return props;
};

export default usePresenter;
